import React, { useCallback, useState, useEffect, useMemo } from 'react';
import AddLoanLeaseCoverage from './AddLoanLeaseCoverage';
import VehicleBundles from './VehicleBundles';
import VehicleCoverageSelection from './VehicleCoverageSelection';
import VehicleForm from './VehicleForm';
import VehicleKillQuestions from './VehicleKillQuestions';
import VehicleMoreInformation from './VehicleMoreInformation';
import { statesByAbbreviation } from '../../../../States';

const AddVehicle = ({ actions, policy, effectiveDate, onCancel, onComplete, save, onError, setLoading, onSave, onExit, previousVehicle, onClearRate, rate, setRate, error }) => {
	const currentEnhancement = useMemo(() => {
		if (policy.hasAutoEnhancement) {
			return 'better';
		} else if (policy.hasAutoPlusEnhancement) {
			return 'best';
		} else {
			return 'no';
		}
	}, [policy]);

	const defaultGaragingAddress = useMemo(() => policy.addresses.filter(a => a.poBox === null || a.poBox === '')[0], [policy])
	const [data, setData] = useState({
		step: 0,
		year: null,
		changedFields: [],
		addBy: true,
		annualMileage: 0,
		vin: '',
		make: '',
		model: '',
		loanOrLease: false,
		addLoanLeaseCoverage: null,
		hasThirdPartyLoanLeaseCoverage: null,
		isOtherAddress: defaultGaragingAddress === null,
		garagingAddressHouseNumber: '',
		garagingAddressStreetName: '',
		garagingAddressApartmentNumber: '',
		garagingAddressPOBox: '',
		garagingAddressStateId: 0,
		garagingAddressZip: '',
		garagingAddressCounty: '',
		garagingAddressCity: '',
		creditorType: 0,
		creditorFirstName: '',
		creditorLastName: '',
		creditorBusinessName: '',
		creditorAddressHouseNumber: '',
		creditorAddressStreetName: '',
		creditorAddressApartmentNumber: '',
		creditorAddressPOBox: '',
		creditorAddressZip: '',
		creditorAddressCity: '',
		creditorAddressStateId: null,
		enhancement: currentEnhancement,
		comprehensive: previousVehicle?.comprehensive || 0,
		collision: previousVehicle?.collision || 0,
		rentalReimbursement: previousVehicle?.rentalReimbursement || 0,
		towingAndLabor: previousVehicle?.towingAndLabor || 0,
		style: null
	});

	const [vehicleState, setVehicleState] = useState({ valid: false, vehicle: null, loading: false });
    const [addressState, setAddressState] = useState({ addressLoading: false, previousZip: null });

    useEffect(() => {
		if (save) {
			setData({ ...data, ...save });
			setVehicleState({ valid: save.vehicle != null, vehicle: save.vehicle, loading: false });
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onVehicleFormChange = useCallback(
		(changedData) => {
			const changedKeys = Object.keys(changedData);
			setData({ ...data, ...changedData, changedFields: [...new Set([...data.changedFields, ...changedKeys])] });
		},
		[data]
	);

	const _onSave = useCallback(
		async (vehicleData, _rate) => {
			vehicleData.vehicleNum = previousVehicle?.vehicleNum;
			vehicleData.vehicle = vehicleState.vehicle;
			await onSave(vehicleData, _rate || rate);
		},
		[onSave, previousVehicle, rate, vehicleState]
	);

	const _onSaveAndExit = useCallback(async () => {
		await _onSave(data, rate);
		onExit();
	}, [_onSave, data, onExit, rate]);

	const [success, setSuccess] = useState(false);

	const onRateConfirm = useCallback(() => {
		onComplete(rate);
		setSuccess(true);
	}, [onComplete, rate]);

	const onRate = useCallback(async (newVehicleState) => {
		try {
			if(newVehicleState) {
				setVehicleState({ vehicle: newVehicleState, valid: newVehicleState != null, loading: false});
				setData({ ...data, vin: newVehicleState.vin, addBy: true });		
			}

			const _vehicleState = newVehicleState ? { vehicle: newVehicleState } : vehicleState;
			setLoading('Generating rate');
			const _vehicle = { vehicleNum: previousVehicle?.vehicleNum || rate?.vehicleNum, year: _vehicleState.vehicle.year, make: _vehicleState.vehicle.make, model: _vehicleState.vehicle.model ,vin:_vehicleState.vehicle.vin};
			let style = null;
			if (_vehicleState.vehicle.vin !== '') {
				style = _vehicleState.vehicle.styles[0];
			} else {
				style = JSON.parse(data.style);
			}
			let rateData = { policyId: policy.policyId, policyNumber: policy.policyNumber, policyEffectiveDate: policy.term.effectiveDate, endorsementPolicyImageNumber: rate?.policyImageNum, effectiveDate, ...data, ..._vehicle, ...style };
			const result = await actions.rateVehicle(rateData);

			const _rate = { ...result.response, email: { action: 'Added', vehicle: _vehicleState.vehicle, comprehensive: data.comprehensive, rentalReimbursement: data.rentalReimbursement, collision: data.collision, towingAndLabor: data.towingAndLabor, previousVehicle } };

			setRate(_rate);
			await _onSave(data, _rate);
			setLoading(null);
		} catch (error) {
			setLoading(null);
			onError(error.error);
			throw error;
		}
	}, [vehicleState, setLoading, previousVehicle, rate, policy, effectiveDate, data, actions, setRate, _onSave, onError]);

	const updateVehicle = useCallback(
		async (vin, year, make, model) => {
			try {
				if (vin.length === 17 || (year > 0 && make !== '' && model !== '')) {
					setVehicleState({ vehicle: null, valid: false, loading: true });
					const result = await actions.vehicleLookup(vin, year, make, model, policy.versionId);
                    setVehicleState({ vehicle: result.response ? { ...result.response, vin } : null, valid: result.response != null, loading: false });
				} else {
					setVehicleState({ vehicle: null, valid: false, loading: false });
				}
			} catch (error) {
				setVehicleState({ vehicle: null, valid: false, loading: false });
			}
		},
		[actions, policy]
	);
	
    const garagingAddressLookup = useCallback(
		async (zipCode) => {
			if (zipCode && zipCode.length === 5 && zipCode !== addressState.previousZip)
			{
				try {
                    setAddressState({ loadingAddress: true });
                    
					const result = await actions.addressLookup(zipCode);
                    if (result) {
						const state = statesByAbbreviation[result.response.stateAbbrev];
						if (data.garagingAddressStateId !== state ||
                            data.garagingAddressCounty !== result.response.county ||
                            data.garagingAddressCity !== result.response.city) {

                            const changedData = {
                                garagingAddressStateId: state,
                                garagingAddressCounty: result.response.county,
                                garagingAddressCity: result.response.city
                            };

                            onVehicleFormChange(changedData);
                        }
                    }
				} catch (error) {
                }
                setAddressState({ loadingAddress: false, previousZip: zipCode });
			}
        },
        [actions, onVehicleFormChange]
    );

	const creditorAddressLookup = useCallback(
		async (zipCode) => {
			if (zipCode && zipCode.length === 5 && zipCode !== addressState.previousZip)
            {
                try {
                    setAddressState({ loadingAddress: true });

                    const result = await actions.addressLookup(zipCode);
                    
                    if (result) {
						const state = statesByAbbreviation[result.response.stateAbbrev];

                        if (data.creditorAddressStateId !== state ||
                            data.creditorAddressCity !== result.response.city) {
							
                            const changedData = {
                                creditorAddressStateId: state,
                                creditorAddressCity: result.response.city
                            };

                            onVehicleFormChange(changedData);
                        }
                    }
                } catch (error) { }
			    setAddressState({ loadingAddress: false, previousZip: zipCode });
            }
        },
        [actions, onVehicleFormChange]
    );

	const onMoreInformationChange = useCallback(
		(changedData) => {
			if (changedData.vin && changedData.vin !== '') {
				changedData.addBy = true;
				changedData.year = null;
				changedData.make = '';
				changedData.model = '';
				changedData.style = null;
			}

			onVehicleFormChange(changedData);
		},
		[onVehicleFormChange]
	);

	const KILLQUESTIONS_STEP = 0;
	const FORM_STEP = 1;
	const MOREINFO_STEP = 2;
	const LOANLEASECOVERAGE_STEP = 3;
	const BUNDLES_STEP = 4;
	const COVERAGE_STEP = 5;

	const onContinue = useCallback(() => {
		const currentStep = data.step;
		var nextStep = currentStep + 1;
		setData({ ...data, step: nextStep });
	}, [data]);

	const onVehicleFormContinue = useCallback(() => {
		var nextStep = MOREINFO_STEP;
		if (!data.loanOrLease) {
			if (currentEnhancement === 'best') {
				nextStep = COVERAGE_STEP;
			} else {
				nextStep = BUNDLES_STEP;
			}
		}

		setData({ ...data, step: nextStep });
	}, [currentEnhancement, data]);

	const onAddLoanLeaseCoverageContinue = useCallback(
		(add) => {
			var nextStep = BUNDLES_STEP;
			if (currentEnhancement === 'best') {
				nextStep = COVERAGE_STEP;
			}
			setData({ ...data, step: nextStep, addLoanLeaseCoverage: add });
		},
		[currentEnhancement, data]
	);

	const onMoreInformationContinue = useCallback(() => {
		var nextStep = LOANLEASECOVERAGE_STEP;

		if (!data.loanOrLease || data.hasThirdPartyLoanLeaseCoverage || data.hasThirdPartyLoanLeaseCoverage === null) {
			onAddLoanLeaseCoverageContinue(false);
			return;
		}

		setData({ ...data, step: nextStep });
	}, [data, onAddLoanLeaseCoverageContinue]);

	const onBundlesContinue = useCallback(
		(enhancement) => {
			var nextStep = COVERAGE_STEP;
			setData({ ...data, step: nextStep, enhancement });
		},
		[data]
	);

	const onBundlesBack = useCallback(() => {
		var previousStep = FORM_STEP;

		if (data.loanOrLease && !data.hasThirdPartyLoanLeaseCoverage && data.hasThirdPartyLoanLeaseCoverage !== null) {
			previousStep = LOANLEASECOVERAGE_STEP;
		} else if (data.loanOrLease) {
			previousStep = MOREINFO_STEP;
		}

		setData({ ...data, step: previousStep });
	}, [data]);

	const onCoverageSelectionBack = useCallback(() => {
		onClearRate();

		var previousStep = BUNDLES_STEP;
		if (currentEnhancement === 'best') {
			onBundlesBack();
			return;
		}

		setData({ ...data, step: previousStep });
	}, [onClearRate, currentEnhancement, data, onBundlesBack]);

	const onBack = useCallback(() => {
		const currentStep = data.step;
		var previousStep = currentStep - 1;
		setData({ ...data, step: previousStep });
	}, [data]);
	
	switch (data.step) {
		default:
		case KILLQUESTIONS_STEP:
			return <VehicleKillQuestions onContinue={onContinue} onCancel={onCancel} />;
		case FORM_STEP:
			return <VehicleForm  vehicleState={vehicleState} updateVehicle={updateVehicle} data={data} defaultGaragingAddress={defaultGaragingAddress} onChange={onVehicleFormChange} onSaveAndExit={_onSaveAndExit} onContinue={onVehicleFormContinue} onCancel={onCancel} onBack={onBack} addressLookup={garagingAddressLookup} addressState={addressState} />;
		case MOREINFO_STEP:
			return <VehicleMoreInformation data={data} onContinue={onMoreInformationContinue} onBack={onBack} onChange={onMoreInformationChange} onSaveAndExit={_onSaveAndExit} updateVehicle={updateVehicle} vehicleState={vehicleState} onCancel={onCancel} addressLookup={creditorAddressLookup} addressState={addressState} />;
		case LOANLEASECOVERAGE_STEP:
			return <AddLoanLeaseCoverage vehicle={vehicleState.vehicle} onBack={onBack} onSaveAndExit={_onSaveAndExit} onContinue={onAddLoanLeaseCoverageContinue} onCancel={onCancel} addLoanLeaseCoverage={data.addLoanLeaseCoverage} />;
		case BUNDLES_STEP:
			return <VehicleBundles onAddBetter={() => onBundlesContinue('better')} onAddBest={() => onBundlesContinue('best')} onNoThanks={() => onBundlesContinue(currentEnhancement)} onBack={onBundlesBack} onSaveAndExit={_onSaveAndExit} currentEnhancement={currentEnhancement} effectiveDate={effectiveDate} policy={policy} />;
		case COVERAGE_STEP:
			return <VehicleCoverageSelection data={data} onChange={onVehicleFormChange} onBack={onCoverageSelectionBack} onCancel={onCancel} onSaveAndExit={_onSaveAndExit} rate={rate} policy={policy} onRate={onRate} onRateConfirm={onRateConfirm} vehicleState={vehicleState} previousVehicle={previousVehicle} success={success && !error} onExit={onExit} currentEnhancement={currentEnhancement} updateVehicle={updateVehicle} actions={actions} onClearRate={onClearRate} effectiveDate={effectiveDate} />;
	}
};

AddVehicle.propTypes = {};

export default AddVehicle;
